import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PORTAL_RELEASE_STATE } from '@lims/shared-types';
import { reportPortalReleaseSchema, reportPortalWithdrawSchema } from '@lims/shared-validation';
import { reportsApi, type ReportDetailView } from '../../api/endpoints.js';
import {
  ActionBar,
  ActionButton,
  Alert,
  Dialog,
  Panel,
  StatusBadge,
  TextAreaField,
  useToast,
} from '../../components/ui/index.js';
import { MAXIMUM_REASON, textProblem, useReportAct } from './report-lifecycle-actions.js';
import { MomentText, PersonAt, ReportRefusalNotice, detailCodes } from './report-parts.js';

/**
 * Releasing a report to the client portal — Phase 11 (D149).
 *
 * RELEASE IS NOT DELIVERY. A released version becomes visible to the client's
 * portal accounts; nothing is sent, and nothing records that anyone looked
 * until a portal download does. WITHDRAWING changes nothing already
 * downloaded. Both acts are `reports.deliver`, both are re-checked by the
 * server, and the buttons follow the view's `can.*` hints only.
 */

/** Refusal codes of the two release routes, named under `reports.portalRelease.errors`. */
const RELEASE_REFUSALS: Readonly<Record<string, string>> = {
  report_not_issued_valid: 'notIssuedValid',
  already_released: 'alreadyReleased',
  not_released: 'notReleased',
};

function PortalReleaseRefusal({ error }: { readonly error: unknown }) {
  const { t } = useTranslation();
  if (!error) return null;
  const named = detailCodes(error).find((code) => RELEASE_REFUSALS[code]);
  if (named) {
    return <Alert tone="warning" title={t(`reports.portalRelease.errors.${RELEASE_REFUSALS[named]}`)} />;
  }
  return <ReportRefusalNotice error={error} />;
}

type Mode = 'release' | 'withdraw';

function PortalReleaseDialog({
  report,
  mode,
  onClose,
}: {
  readonly report: ReportDetailView;
  readonly mode: Mode;
  readonly onClose: () => void;
}) {
  const { t } = useTranslation();
  const { show } = useToast();
  const [text, setText] = useState('');
  const [touched, setTouched] = useState(false);
  // A withdrawal needs a reason; a release note is optional.
  const problem =
    mode === 'withdraw'
      ? textProblem(text, MAXIMUM_REASON)
      : text.trim().length > MAXIMUM_REASON
        ? 'validation.tooLong'
        : null;

  const act = useReportAct(
    (body: { note?: string; reason?: string }) =>
      mode === 'release'
        ? reportsApi.portalRelease(report.id, body.note ? { note: body.note } : {})
        : reportsApi.withdrawPortalRelease(report.id, { reason: body.reason ?? '' }),
    (answer) => {
      const state = answer.portalRelease?.state ?? PORTAL_RELEASE_STATE.NOT_RELEASED;
      // What the server now says, never what the button implied.
      show({ tone: 'success', message: t(`reports.portalRelease.state.${state}`) });
      onClose();
    },
  );

  const submit = () => {
    setTouched(true);
    if (problem) return;
    const trimmed = text.trim();
    if (mode === 'release') {
      const body = trimmed ? { note: trimmed } : {};
      if (!reportPortalReleaseSchema.safeParse(body).success) return;
      act.mutate(body);
    } else {
      const body = { reason: trimmed };
      if (!reportPortalWithdrawSchema.safeParse(body).success) return;
      act.mutate(body);
    }
  };

  const title = mode === 'release' ? t('reports.portalRelease.release') : t('reports.portalRelease.withdraw');

  return (
    <Dialog
      isOpen
      onClose={onClose}
      title={title}
      footer={
        <ActionBar>
          <ActionButton priority="tertiary" onPress={onClose} isDisabled={act.isPending}>
            {t('common.cancel')}
          </ActionButton>
          <ActionButton
            priority="primary"
            isDisabled={problem !== null}
            isLoading={act.isPending}
            onPress={submit}
          >
            {title}
          </ActionButton>
        </ActionBar>
      }
    >
      <div className="flex flex-col gap-stack">
        <Alert
          tone="info"
          title={
            mode === 'release'
              ? t('reports.portalRelease.releaseNoticeTitle')
              : t('reports.portalRelease.withdrawNoticeTitle')
          }
          description={
            mode === 'release'
              ? t('reports.portalRelease.releaseNoticeBody')
              : t('reports.portalRelease.withdrawNoticeBody')
          }
        />
        <TextAreaField
          label={mode === 'release' ? t('reports.portalRelease.note') : t('common.reason')}
          value={text}
          onChange={(value) => {
            setText(value);
            setTouched(true);
          }}
          isRequired={mode === 'withdraw'}
          rows={3}
          {...(touched && problem ? { error: t(problem) } : {})}
        />
        <PortalReleaseRefusal error={act.error} />
      </div>
    </Dialog>
  );
}

/** The portal panel of a report: state, actions, history, downloads. */
export function PortalReleasePanel({ report }: { readonly report: ReportDetailView }) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<Mode | null>(null);

  const release = report.portalRelease;
  const state = release?.state ?? PORTAL_RELEASE_STATE.NOT_RELEASED;
  const downloads = report.portalDownloads ?? [];

  return (
    <Panel title={t('reports.portalRelease.title')} description={t('reports.portalRelease.hint')}>
      <div className="flex flex-col gap-stack">
        <div className="flex flex-wrap items-center gap-inline">
          <StatusBadge
            label={t(`reports.portalRelease.state.${state}`)}
            tone={state === PORTAL_RELEASE_STATE.RELEASED ? 'info' : 'neutral'}
          />
          {release?.versionNumber ? (
            <span className="text-caption text-default-600">
              {t('reports.portalRelease.version', { number: release.versionNumber })}
            </span>
          ) : null}
        </div>
        <p className="text-caption text-default-600">{t('reports.portalRelease.notDelivery')}</p>

        {report.can.releaseToPortal || report.can.withdrawFromPortal ? (
          <ActionBar>
            {report.can.releaseToPortal ? (
              <ActionButton priority="secondary" size="sm" onPress={() => setMode('release')}>
                {t('reports.portalRelease.release')}
              </ActionButton>
            ) : null}
            {report.can.withdrawFromPortal ? (
              <ActionButton priority="tertiary" size="sm" onPress={() => setMode('withdraw')}>
                {t('reports.portalRelease.withdraw')}
              </ActionButton>
            ) : null}
          </ActionBar>
        ) : null}

        {(release?.history.length ?? 0) > 0 ? (
          <section className="flex flex-col gap-1">
            <h3 className="text-caption font-medium text-default-700">
              {t('reports.portalRelease.history')}
            </h3>
            <ul className="flex flex-col divide-y divide-default-100" data-testid="portal-release-history">
              {release?.history.map((event, index) => (
                <li
                  key={`${event.actedAt}:${index}`}
                  className="flex flex-col py-1.5 text-caption text-default-700"
                >
                  <span>
                    {t(`reports.portalRelease.action.${event.action}`)} ·{' '}
                    {t('reports.portalRelease.version', { number: event.versionNumber })} ·{' '}
                    <PersonAt person={event.actedBy} at={event.actedAt} />
                  </span>
                  {event.note || event.reason ? (
                    <bdi className="whitespace-pre-wrap text-default-600">{event.reason ?? event.note}</bdi>
                  ) : null}
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        <section className="flex flex-col gap-1">
          <h3 className="text-caption font-medium text-default-700">
            {t('reports.portalRelease.downloads')}
          </h3>
          {downloads.length === 0 ? (
            <p className="text-caption text-default-600">{t('reports.portalRelease.noDownloads')}</p>
          ) : (
            <ul className="flex flex-col gap-1 text-caption text-default-700" data-testid="portal-downloads">
              {downloads.map((download, index) => (
                <li key={`${download.occurredAt}:${index}`}>
                  <bdi>{download.accountDisplayName ?? t('common.notRecorded')}</bdi>
                  {' · '}
                  {t('reports.portalRelease.version', { number: download.versionNumber })}
                  {' · '}
                  <MomentText at={download.occurredAt} />
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
      {mode ? <PortalReleaseDialog report={report} mode={mode} onClose={() => setMode(null)} /> : null}
    </Panel>
  );
}
